import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const refreshTimeoutRef = useRef(null);
  const idleTimeoutRef = useRef(null);

  // Функція логауту
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    setUser(null);
    
    // Очищаємо всі таймери
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
  };

  // Перевірка валідності токена
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  };

  // Отримання часу до закінчення токена (в секундах)
  const getTokenTimeLeft = (token) => {
    if (!token) return 0;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return Math.max(0, payload.exp - currentTime);
    } catch {
      return 0;
    }
  };

  // Функція оновлення токена
  const refreshToken = async () => {
    const token = localStorage.getItem("token");
    if (!token || !isTokenValid(token)) {
      logout();
      return;
    }

    try {
      const response = await axios.post('/api/refresh-token', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        setUser(prev => prev ? { ...prev, token: response.data.token } : null);
        console.log("Токен успішно оновлено");
        
        // Плануємо наступне оновлення
        scheduleTokenRefresh(response.data.token);
      }
    } catch (error) {
      console.error("Помилка оновлення токена:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    }
  };

  // Планування оновлення токена (за 5 хвилин до закінчення)
  const scheduleTokenRefresh = (token) => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    
    const timeLeft = getTokenTimeLeft(token);
    const refreshTime = Math.max(0, (timeLeft - 300) * 1000); // 5 хвилин до закінчення
    
    if (refreshTime > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshToken();
      }, refreshTime);
    }
  };

  // Відстеження активності користувача
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
    
    // Скидаємо таймер неактивності
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    
    // Встановлюємо новий таймер на 1 годину
    idleTimeoutRef.current = setTimeout(() => {
      console.log("Користувач неактивний протягом години, виконуємо логаут");
      logout();
      alert("Ви були автоматично вилоговані через неактивність. Будь ласка, увійдіть знову.");
    }, 60 * 60 * 1000); // 1 година
  };

  // Додавання слухачів подій для відстеження активності
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => updateActivity();
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");

      if (!token || !role) {
        setLoading(false);
        return;
      }

      if (!isTokenValid(token)) {
        logout();
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/user/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUser({ ...response.data, role, token });
        
        // Плануємо оновлення токена
        scheduleTokenRefresh(token);
        
        // Встановлюємо таймер неактивності
        updateActivity();
        
      } catch (error) {
        console.error("Помилка при отриманні користувача:", error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
